import { Num } from "@/components/Num";
import { useEffect, useState } from "react";
import { Sparkles, MessageSquare, TrendingUp, TrendingDown, Zap, AlertTriangle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BepCard } from "@/components/BepCard";
import { Textarea } from "@/components/ui/textarea";
import { api, errorText } from "@/lib/api";
import { MONTHS, fmtPct } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const Cell = ({ v, pct, invert }) => {
  const good = pct === null || pct === undefined ? null : invert ? pct <= 0 : pct >= 0;
  return (
    <td className={`px-2 py-1.5 text-right font-mono whitespace-nowrap ${good === null ? "text-slate-500" : good ? "text-emerald-700" : "text-red-600"}`}>
      <Num v={v} compact /> <span className="text-[10px]">({fmtPct(pct)})</span>
    </td>
  );
};

const KpiStrip = ({ kpis }) => {
  const rows = ["revenue", "gop", "payroll", "ebitda"].map((k) => kpis[k] && { k, ...kpis[k] }).filter(Boolean);
  const gm = kpis.gop_margin;
  const pt = (a, b) => (a === null || b === null || a === undefined || b === undefined ? "-" : `${(a - b) >= 0 ? "+" : ""}${(a - b).toFixed(2)} pt`);
  return (
    <div className="overflow-auto rounded-lg border border-slate-200" data-testid="flash-view-kpis">
      <table className="min-w-full text-xs">
        <thead><tr className="bg-emerald-700 text-white text-[10px] uppercase tracking-wider">
          <th className="text-left px-2 py-2">KPI</th><th className="px-2 py-2 text-right">Month Actual</th><th className="px-2 py-2 text-right">vs Budget</th><th className="px-2 py-2 text-right">Month Forecast</th><th className="px-2 py-2 text-right">vs Forecast</th>
          <th className="px-2 py-2 text-right">YTD Actual</th><th className="px-2 py-2 text-right">vs Budget</th><th className="px-2 py-2 text-right">FY Forecast</th><th className="px-2 py-2 text-right">vs FY Budget</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.k} className="border-t border-slate-100" data-testid={`flash-view-kpi-${r.k}`}>
              <td className="px-2 py-1.5 font-semibold text-slate-900">{r.label}</td>
              <td className="px-2 py-1.5 text-right font-mono"><Num v={r.m_actual} compact /></td><Cell v={r.m_var} pct={r.m_var_pct} invert={r.k === "payroll"} />
              <td className="px-2 py-1.5 text-right font-mono text-slate-500" data-testid={`flash-kpi-fcst-${r.k}`}><Num v={r.m_forecast} compact /></td><Cell v={r.m_var_fcst} pct={r.m_var_fcst_pct} invert={r.k === "payroll"} />
              <td className="px-2 py-1.5 text-right font-mono"><Num v={r.ytd_actual} compact /></td><Cell v={r.ytd_var} pct={r.ytd_var_pct} invert={r.k === "payroll"} />
              <td className="px-2 py-1.5 text-right font-mono"><Num v={r.fy_reforecast} compact /></td><Cell v={r.fy_var} pct={r.fy_var_pct} invert={r.k === "payroll"} />
            </tr>
          ))}
          {gm && <tr className="border-t border-slate-100 bg-slate-50" data-testid="flash-view-kpi-gop-margin">
            <td className="px-2 py-1.5 font-semibold text-slate-900">GOP Margin</td>
            <td className="px-2 py-1.5 text-right font-mono">{gm.m_actual?.toFixed(2) ?? "-"}%</td><td className="px-2 py-1.5 text-right font-mono text-slate-500">{pt(gm.m_actual, gm.m_budget)}</td>
            <td className="px-2 py-1.5 text-right font-mono">{gm.ytd_actual?.toFixed(2) ?? "-"}%</td><td className="px-2 py-1.5 text-right font-mono text-slate-500">{pt(gm.ytd_actual, gm.ytd_budget)}</td>
            <td className="px-2 py-1.5 text-right font-mono">{gm.fy_reforecast?.toFixed(2) ?? "-"}%</td><td className="px-2 py-1.5 text-right font-mono text-slate-500">{pt(gm.fy_reforecast, gm.fy_budget)}</td>
          </tr>}
        </tbody>
      </table>
    </div>
  );
};

const AlertRow = ({ a, projectId, month, canEdit, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(a.comment?.text || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/tracking/comment`, { line_id: a.id, month, text });
      toast.success("Variance commentary saved");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const cost = !/^revenue/.test(a.driver || "");
  const good = cost ? a.m_var <= 0 : a.m_var >= 0;
  return (
    <li className={`py-2.5 text-sm ${a.comment ? "" : "bg-amber-50/60 -mx-4 px-4"}`} data-testid={`flash-alert-${a.id}`}>
      <div className="flex items-center gap-3">
        <AlertTriangle size={14} className={`shrink-0 ${a.comment ? "text-slate-300" : "text-amber-600"}`} />
        <div className="min-w-0 flex-1">
          <p className="text-slate-900 truncate"><span className="text-slate-400 text-xs">{a.sheet} /</span> {a.label}</p>
          <p className="text-[11px] text-slate-500 font-mono">actual <Num v={a.m_actual} /> · budget <Num v={a.m_budget} /></p>
        </div>
        {a.m_forecast !== null && a.m_forecast !== undefined && <span className="text-[11px] font-mono text-slate-500 whitespace-nowrap" data-testid={`flash-alert-fcst-${a.id}`}>Fcst <Num v={a.m_forecast} compact /> · <span className={a.m_var_fcst_pct >= 0 ? "text-emerald-700" : "text-red-600"}>{fmtPct(a.m_var_fcst_pct)}</span> vs fcst</span>}
        <p className={`font-mono text-sm font-semibold whitespace-nowrap ${good ? "text-emerald-700" : "text-red-600"}`}><Num v={a.m_var} compact /> <span className="text-xs">({fmtPct(a.m_var_pct)})</span></p>
        {canEdit && !open && <Button size="sm" variant={a.comment ? "ghost" : "outline"} onClick={() => setOpen(true)} className={`h-7 text-xs shrink-0 ${a.comment ? "" : "border-amber-400 text-amber-800 hover:bg-amber-100"}`} data-testid={`flash-alert-comment-button-${a.id}`}>
          <MessageSquare size={12} className="mr-1" /> {a.comment ? "Edit" : "Add commentary"}
        </Button>}
      </div>
      {a.comment && !open && <p className="mt-1.5 ml-7 text-slate-700 text-xs" data-testid={`flash-alert-comment-text-${a.id}`}>{a.comment.text} <span className="text-slate-400 font-mono">· {a.comment.by}</span></p>}
      {!a.comment && !open && <p className="mt-1 ml-7 text-[11px] text-amber-700 font-medium" data-testid={`flash-alert-pending-${a.id}`}>Commentary pending — explain the root cause and action plan.</p>}
      {open && (
        <div className="mt-2 ml-7 space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Root cause and action plan..." className="min-h-[70px] text-sm bg-white" data-testid={`flash-alert-textarea-${a.id}`} />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="h-7 text-xs">Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy || !text.trim()} className="h-7 text-xs bg-slate-900 hover:bg-emerald-700" data-testid={`flash-alert-save-${a.id}`}>{busy ? "Saving..." : "Save commentary"}</Button>
          </div>
        </div>
      )}
    </li>
  );
};

export const FlashViewSheet = ({ target, onClose }) => {
  const [month, setMonth] = useState(null);
  const [threshold, setThreshold] = useState(10);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const { can } = useAuth();
  useEffect(() => { setMonth(target?.month ?? null); setData(null); }, [target]);
  useEffect(() => {
    if (!target) return;
    api.get(`/projects/${target.projectId}/flash-report`, { params: { ...(month ? { month } : {}), threshold } }).then((r) => { setData(r.data); if (!month) setMonth(r.data.month); }).catch((e) => toast.error(errorText(e)));
  }, [target, month, threshold, tick]);
  const gen = async () => {
    setBusy(true);
    try {
      const { data: s } = await api.post(`/projects/${target.projectId}/flash-report/summary`, null, { params: { month }, timeout: 120000 });
      setData((d) => ({ ...d, summary: s }));
      toast.success("AI executive summary generated");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!target} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-4xl overflow-y-auto bg-[#F8F9FA]" data-testid="flash-view-sheet">
        <SheetHeader className="text-left">
          <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold inline-flex items-center gap-1"><Zap size={12} /> Executive flash view</p>
          <SheetTitle className="font-heading text-2xl">{target?.name}</SheetTitle>
          <SheetDescription>{data ? `${data.month_label} · actual vs ${data.reference} budget vs reforecast (actual YTD + remaining budget)` : "Loading..."}</SheetDescription>
        </SheetHeader>
        {data && (
          <div className="mt-4 space-y-5">
            <select value={month || data.month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="flash-view-month-select" className="h-9 rounded-md border border-slate-300 bg-white text-slate-900 px-3 text-xs">
              {data.tracked_months.map((m) => <option key={m} value={m}>{MONTHS[m - 1]} {data.year}</option>)}
            </select>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4" data-testid="flash-view-summary">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold inline-flex items-center gap-1"><Sparkles size={12} /> AI executive summary</p>
                {can("edit") && <Button size="sm" variant="outline" onClick={gen} disabled={busy} className="h-7 text-xs" data-testid="flash-view-generate-button"><Sparkles size={12} className="mr-1" /> {busy ? "Writing..." : data.summary ? "Regenerate" : "Generate"}</Button>}
              </div>
              {data.summary ? (
                <>
                  {data.summary.text.split("\n").filter((x) => x.trim()).map((x, i) => <p key={i} className="text-sm text-slate-800 leading-relaxed mt-2">{x}</p>)}
                  <p className="text-[10px] text-slate-500 mt-2 font-mono">{data.summary.model} · {data.summary.by} · {new Date(data.summary.at).toLocaleString("en-GB")}</p>
                </>
              ) : <p className="text-sm text-slate-500 mt-2">No summary yet for this month.{can("edit") ? " Click Generate to let the AI write the executive summary." : ""}</p>}
            </div>
            <div className={`rounded-lg border p-4 ${data.alerts_summary.uncommented ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"}`} data-testid="flash-view-alerts">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="font-heading text-sm font-semibold text-slate-900 inline-flex items-center gap-2"><AlertTriangle size={14} className="text-amber-600" /> Variance alerts · {MONTHS[data.month - 1]}
                  <Badge className={`border-0 ${data.alerts_summary.uncommented ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-700"}`} data-testid="flash-view-alerts-badge">{data.alerts_summary.total} alert{data.alerts_summary.total === 1 ? "" : "s"} · {data.alerts_summary.uncommented} without commentary</Badge>
                </h4>
                <label className="text-xs text-slate-600 inline-flex items-center gap-1">Threshold <input type="number" min="1" step="1" value={threshold} onChange={(e) => setThreshold(Number(e.target.value) || 0)} data-testid="flash-view-threshold-input" className="w-16 h-7 rounded border border-slate-300 px-2 font-mono text-xs bg-white" /> %</label>
              </div>
              <ul className="mt-2 divide-y divide-slate-100">
                {data.alerts.map((a) => <AlertRow key={a.id} a={a} projectId={target.projectId} month={data.month} canEdit={can("edit")} onSaved={() => setTick((t) => t + 1)} />)}
                {!data.alerts.length && <li className="py-2 text-sm text-slate-400">No line variance above {threshold}% this month.</li>}
              </ul>
            </div>
            <KpiStrip kpis={data.kpis} />
            <BepCard projectId={target.projectId} compact />
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="flash-view-top-variances">
              <h4 className="font-heading text-sm font-semibold text-slate-900">Largest line variances · {MONTHS[data.month - 1]}</h4>
              <ul className="mt-2 divide-y divide-slate-100">
                {data.top_variances.map((t) => {
                  const cost = !/^revenue/.test(t.driver || "");
                  const good = cost ? t.m_var <= 0 : t.m_var >= 0;
                  return (
                    <li key={t.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`flash-view-variance-${t.id}`}>
                      {good ? <TrendingUp size={14} className="text-emerald-600 shrink-0" /> : <TrendingDown size={14} className="text-red-600 shrink-0" />}
                      <div className="min-w-0 flex-1"><p className="text-slate-900 truncate"><span className="text-slate-400 text-xs">{t.sheet} /</span> {t.label}</p><p className="text-[11px] text-slate-500 font-mono">actual <Num v={t.m_actual} /> · budget <Num v={t.m_budget} /></p></div>
                      <p className={`font-mono text-sm font-semibold whitespace-nowrap ${good ? "text-emerald-700" : "text-red-600"}`}><Num v={t.m_var} compact /> <span className="text-xs">({fmtPct(t.m_var_pct)})</span></p>
                    </li>
                  );
                })}
                {!data.top_variances.length && <li className="py-2 text-sm text-slate-400">No line variances for this month.</li>}
              </ul>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="flash-view-comments">
              <h4 className="font-heading text-sm font-semibold text-slate-900 inline-flex items-center gap-2"><MessageSquare size={14} className="text-emerald-600" /> Variance commentary</h4>
              <ul className="mt-2 divide-y divide-slate-100">
                {data.comments.map((c) => (
                  <li key={c.line_id} className="py-2.5 text-sm" data-testid={`flash-view-comment-${c.line_id}`}>
                    <p className="text-slate-900"><span className="font-semibold">{c.sheet} / {c.label}</span> · <Num v={c.actual} line={c} /> vs <Num v={c.budget} line={c} /> <span className={c.var >= 0 ? "text-emerald-700" : "text-red-600"}>({fmtPct(c.var_pct)})</span></p>
                    <p className="text-slate-700 mt-0.5">{c.text}</p>
                    <p className="text-[11px] text-slate-500 font-mono mt-0.5">{c.by} · {c.at ? new Date(c.at).toLocaleString("en-GB") : ""}</p>
                  </li>
                ))}
                {!data.comments.length && <li className="py-2 text-sm text-slate-400">No controller commentary recorded for this month.</li>}
              </ul>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
